import { useQuery } from '@tanstack/react-query';
import { formatPkr } from '@raahi/shared';
import { Shell } from '../layout/Shell.js';
import { Skeleton } from '../components/Skeleton.js';
import { EmptyState } from '../components/EmptyState.js';
import { getEarnings } from '../api/driver.js';

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-PK', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function KpiSkeleton() {
  return (
    <div className="card p-3">
      <Skeleton className="h-3 w-12 block" />
      <Skeleton className="mt-2 h-6 w-20 block" />
    </div>
  );
}

function TripSkeleton() {
  return (
    <li className="card p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-2">
          <Skeleton className="h-3 w-24 block" />
          <Skeleton className="h-4 w-32 block" />
          <Skeleton className="h-3 w-24 block" />
        </div>
        <Skeleton className="h-4 w-16 block" />
      </div>
    </li>
  );
}

export function EarningsPage() {
  const earnings = useQuery({ queryKey: ['earnings'], queryFn: getEarnings });

  const recent = earnings.data?.recent ?? [];

  return (
    <Shell>
      <div className="px-4 pt-[max(env(safe-area-inset-top),16px)]">
        <h1 className="font-display text-2xl font-bold">Earnings</h1>
        <p className="text-sm text-muted">Your trips and totals.</p>

        <div className="mt-4 grid grid-cols-3 gap-2">
          {earnings.isLoading ? (
            <>
              <KpiSkeleton />
              <KpiSkeleton />
              <KpiSkeleton />
            </>
          ) : (
            <>
              <div className="card p-3">
                <p className="text-xs uppercase tracking-wide text-muted">Today</p>
                <p className="num mt-1 font-display text-xl font-semibold">{formatPkr(earnings.data?.today ?? 0)}</p>
              </div>
              <div className="card p-3">
                <p className="text-xs uppercase tracking-wide text-muted">This week</p>
                <p className="num mt-1 font-display text-xl font-semibold">{formatPkr(earnings.data?.week ?? 0)}</p>
              </div>
              <div className="card p-3">
                <p className="text-xs uppercase tracking-wide text-muted">Lifetime</p>
                <p className="num mt-1 font-display text-xl font-semibold">{formatPkr(earnings.data?.lifetime ?? 0)}</p>
              </div>
            </>
          )}
        </div>

        <h2 className="mt-6 text-xs font-semibold uppercase tracking-wide text-muted">Recent trips</h2>
        {earnings.isLoading ? (
          <ul className="mt-2 space-y-2">
            <TripSkeleton />
            <TripSkeleton />
            <TripSkeleton />
          </ul>
        ) : recent.length === 0 ? (
          <EmptyState
            title="No completed trips yet"
            description="Once you complete your first trip, it will appear here."
          />
        ) : (
          <ul className="mt-2 space-y-2">
            {recent.map((t) => (
              <li key={t.id} className="card p-3">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs text-muted">{formatDate(t.endedAt)}</p>
                    <p className="font-display text-base font-semibold">{t.pickupLabel}</p>
                    <p className="text-sm text-muted">to {t.dropLabel}</p>
                  </div>
                  <p className="num font-display text-base font-semibold">{formatPkr(t.fareTotal)}</p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Shell>
  );
}
